/**
 * shareFeatures.ts
 * 共有地物（AI分析結果の共有）関連メソッド
 * App.vue から切り出し
 */

import * as turf from '@turf/turf'

type VM = any

// URL共有パラメータを処理（AI瞬間レポート / AI防災リスク診断 / AI人口プロファイル）
// ※マルチ地物共有はsパラメータ経由でaishareパラメータとして復元される
export function handleAiShareUrl(vm: VM): void {
  const params = new URLSearchParams(window.location.search)
  const aiType = params.get('ai')
  if (!aiType) return

  // AI瞬間レポート: ?ai=instant&tab=realestate&c=139.7,35.6
  if (aiType === 'instant') {
    const tab = params.get('tab') || 'realestate'
    const coords = params.get('c')
    if (coords) {
      const [lng, lat] = coords.split(',').map(Number)
      if (!isNaN(lng) && !isNaN(lat)) {
        // 地図を移動
        vm.$store.state.map.flyTo({ center: [lng, lat], zoom: 16 })
        // ダイアログを開く
        vm.$store.commit('addFWInstance', {
          type: 'ai-point-info',
          data: { lng, lat, tab }
        })
        // URLパラメータをクリア（履歴を汚さない）
        window.history.replaceState({}, '', window.location.pathname)
      }
    }
  }
  // AI防災リスク診断: ?ai=risk&gj=BASE64_ENCODED_GEOJSON
  if (aiType === 'risk') {
    const gjBase64 = params.get('gj')
    if (gjBase64) {
      try {
        const gjJson = atob(gjBase64)
        const geojson = JSON.parse(gjJson)
        // GeoJSONの種類を判定（Point or Polygon）
        const geometryType = geojson.geometry?.type || geojson.type
        const isPoint = geometryType === 'Point'
        // 地図を移動（中心座標を計算）
        let center: number[] | undefined
        if (isPoint) {
          center = geojson.geometry?.coordinates || geojson.coordinates
        } else {
          // Polygonの場合は重心を計算
          const coords = geojson.geometry?.coordinates?.[0] || geojson.coordinates?.[0]
          if (coords && coords.length > 0) {
            const sum = coords.reduce((acc: number[], c: number[]) => [acc[0] + c[0], acc[1] + c[1]], [0, 0])
            center = [sum[0] / coords.length, sum[1] / coords.length]
          }
        }
        if (center) {
          vm.$store.state.map.flyTo({ center, zoom: 16 })
        }
        // ダイアログを開く
        vm.$store.commit('addFWInstance', {
          type: 'area-analysis',
          data: {
            polygonData: isPoint ? null : geojson,
            pointData: isPoint ? geojson : null,
            analysisMode: isPoint ? 'point' : 'polygon'
          }
        })
        // URLパラメータをクリア
        window.history.replaceState({}, '', window.location.pathname)
      } catch (e) {
        console.error('AI防災リスク診断URL復元エラー:', e)
      }
    }
  }
  // AI人口プロファイル: ?ai=population&c=139.7,35.6
  if (aiType === 'population') {
    const coords = params.get('c')
    if (coords) {
      const [lng, lat] = coords.split(',').map(Number)
      if (!isNaN(lng) && !isNaN(lat)) {
        // 地図を移動
        vm.$store.state.map.flyTo({ center: [lng, lat], zoom: 16 })
        // ダイアログを開く
        vm.$store.commit('addFWInstance', {
          type: 'super-population',
          data: { lng, lat }
        })
        // URLパラメータをクリア
        window.history.replaceState({}, '', window.location.pathname)
      }
    }
  }
  // AI物件調査レポート+ (mprd) は ?s=xxx ショートURL経由で復元される (initApp.ts 内)
  // ここでは扱わない
}

// AI分析用のURL共有パラメータを更新
export function updateAiShareUrl(vm: VM, type: string, data: any): void {
  let params = ''
  if (type === 'instant' && data.lng && data.lat) {
    params = `?ai=instant&tab=${data.tab || 'realestate'}&c=${data.lng.toFixed(6)},${data.lat.toFixed(6)}`
  }
  if (type === 'risk' && data.geojson) {
    const gjBase64 = btoa(JSON.stringify(data.geojson))
    params = `?ai=risk&gj=${gjBase64}`
  }
  if (type === 'population' && data.lng && data.lat) {
    params = `?ai=population&c=${data.lng.toFixed(6)},${data.lat.toFixed(6)}`
  }
  if (params) {
    window.history.replaceState({}, '', params)
  }
}

// 共有地物を地図に表示
export function showShareFeaturesOnMap(vm: VM): void {
  const map = vm.$store.state.map01
  if (!map) return

  const sourceId = 'oh-ai-share'
  const pointLayerId = 'oh-ai-share-points'
  const pointLabelLayerId = 'oh-ai-share-point-labels'
  const polygonLayerId = 'oh-ai-share-polygons'
  const polygonOutlineId = 'oh-ai-share-polygon-outlines'
  const polygonLabelLayerId = 'oh-ai-share-polygon-labels'

  const pointAddressLabelLayerId = 'oh-ai-share-point-address-labels'

  // shareFeaturesが空の場合は既存レイヤーを削除して終了
  if (vm.shareFeatures.length === 0) {
    if (map.getLayer(pointAddressLabelLayerId)) map.removeLayer(pointAddressLabelLayerId)
    if (map.getLayer(pointLabelLayerId)) map.removeLayer(pointLabelLayerId)
    if (map.getLayer(pointLayerId)) map.removeLayer(pointLayerId)
    if (map.getLayer(polygonLabelLayerId)) map.removeLayer(polygonLabelLayerId)
    if (map.getLayer(polygonOutlineId)) map.removeLayer(polygonOutlineId)
    if (map.getLayer(polygonLayerId)) map.removeLayer(polygonLayerId)
    if (map.getSource(sourceId)) map.removeSource(sourceId)
    vm.$store.commit('setShareFeaturesLayerAdded', false)
    return
  }

  // GeoJSON FeatureCollectionを構築（ナンバリング付き - 同タイプ内の連番を使用）
  const geojson = {
    type: 'FeatureCollection',
    features: vm.shareFeatures.map((f: any, index: number) => ({
      type: 'Feature',
      geometry: f.geometry,
      properties: { ...f.properties, id: f.id, aiType: f.aiType, number: (f.typeIndex ?? index) + 1 }
    }))
  }

  // ソースが既に存在する場合はsetDataで更新し、レイヤーを最前面に移動
  const existingSource = map.getSource(sourceId)
  if (existingSource) {
    existingSource.setData(geojson)
    // レイヤーを最前面に移動（他のレイヤーの下に隠れないように）
    if (map.getLayer(polygonLayerId)) map.moveLayer(polygonLayerId)
    if (map.getLayer(polygonOutlineId)) map.moveLayer(polygonOutlineId)
    if (map.getLayer(polygonLabelLayerId)) map.moveLayer(polygonLabelLayerId)
    if (map.getLayer(pointLayerId)) map.moveLayer(pointLayerId)
    if (map.getLayer(pointLabelLayerId)) map.moveLayer(pointLabelLayerId)
    if (map.getLayer(pointAddressLabelLayerId)) map.moveLayer(pointAddressLabelLayerId)
    return
  }

  // ソースを新規追加
  map.addSource(sourceId, { type: 'geojson', data: geojson })

  // ポリゴンレイヤー（aiTypeで色分け）
  map.addLayer({
    id: polygonLayerId,
    type: 'fill',
    source: sourceId,
    filter: ['==', ['geometry-type'], 'Polygon'],
    paint: {
      'fill-color': [
        'match', ['get', 'aiType'],
        'population', '#9C27B0', // AI人口プロファイル: 紫
        'risk', '#e53935',       // AI防災リスク診断: 赤
        '#2196f3'                // デフォルト: 青
      ],
      'fill-opacity': 0.25
    }
  })

  // ポリゴン枠線レイヤー（aiTypeで色分け）
  map.addLayer({
    id: polygonOutlineId,
    type: 'line',
    source: sourceId,
    filter: ['==', ['geometry-type'], 'Polygon'],
    paint: {
      'line-color': [
        'match', ['get', 'aiType'],
        'population', '#7B1FA2', // AI人口プロファイル: 濃い紫
        'risk', '#c62828',       // AI防災リスク診断: 濃い赤
        '#1565c0'                // デフォルト: 濃い青
      ],
      'line-width': 2.5
    }
  })

  // ポイントレイヤー（aiTypeで色分け + 選択時ハイライト）
  map.addLayer({
    id: pointLayerId,
    type: 'circle',
    source: sourceId,
    filter: ['==', ['geometry-type'], 'Point'],
    paint: {
      'circle-radius': [
        'case',
        ['==', ['get', 'selected'], true], 20,
        16
      ],
      'circle-color': [
        'match', ['get', 'aiType'],
        'instant', '#1976d2',              // AI瞬間レポート: 青
        'population', '#9C27B0',           // AI人口プロファイル: 紫
        'risk', '#e53935',                 // AI防災リスク診断: 赤
        'childcare', '#E91E63',            // AI子育てレポート: ピンク
        'property-report', '#FF9800',      // AI物件調査レポート: オレンジ
        'multiPointRanking', 'rgb(57, 68, 84)', // AI物件調査レポート+: OH3スレート
        '#9c27b0'                          // デフォルト: 紫
      ],
      'circle-stroke-color': [
        'case',
        ['==', ['get', 'selected'], true], '#FFC107', // 選択時: 黄色ハイライト
        '#fff'
      ],
      'circle-stroke-width': [
        'case',
        ['==', ['get', 'selected'], true], 5,
        3
      ]
    }
  })

  // ポイント用ナンバリングラベルレイヤー (番号はマーカー中央)
  // - multiPointRanking: properties.rank (ランキング順位) を表示。未設定なら何も表示しない (黒丸のみ)
  // - その他の aiType: 従来通り properties.number (入力順) を表示
  map.addLayer({
    id: pointLabelLayerId,
    type: 'symbol',
    source: sourceId,
    filter: ['==', ['geometry-type'], 'Point'],
    layout: {
      'text-field': [
        'case',
        ['==', ['get', 'aiType'], 'multiPointRanking'],
        // multiPointRanking は rank を全角数字で表示 (円内に大きく見せる)
        [
          'case',
          ['==', ['get', 'rank'], 1], '１',
          ['==', ['get', 'rank'], 2], '２',
          ['==', ['get', 'rank'], 3], '３',
          ['==', ['get', 'rank'], 4], '４',
          ['==', ['get', 'rank'], 5], '５',
          ''
        ],
        // その他は従来通り番号のみ
        ['to-string', ['get', 'number']]
      ],
      'text-size': 16,
      'text-font': ['Noto Sans CJK JP Bold'],
      'text-allow-overlap': true
    },
    paint: {
      'text-color': '#fff'
    }
  })

  // 住所ラベルレイヤー (multiPointRanking 専用、マーカー下に表示)
  // pointAddressLabelLayerId は関数冒頭で宣言済み
  map.addLayer({
    id: pointAddressLabelLayerId,
    type: 'symbol',
    source: sourceId,
    filter: [
      'all',
      ['==', ['geometry-type'], 'Point'],
      ['==', ['get', 'aiType'], 'multiPointRanking'],
      ['has', 'shortAddress']
    ],
    layout: {
      'text-field': ['get', 'shortAddress'],
      'text-size': 11,
      'text-font': ['Noto Sans CJK JP Bold'],
      'text-offset': [0, 1.4],
      'text-anchor': 'top',
      'text-allow-overlap': false,
      'text-ignore-placement': false
    },
    paint: {
      'text-color': 'rgb(57, 68, 84)',
      'text-halo-color': '#ffffff',
      'text-halo-width': 2.5
    }
  })

  // ポリゴン用ナンバリングラベルレイヤー（ポリゴンの重心に表示）
  // population タイプの場合は人口も表示
  map.addLayer({
    id: polygonLabelLayerId,
    type: 'symbol',
    source: sourceId,
    filter: ['==', ['geometry-type'], 'Polygon'],
    layout: {
      'text-field': [
        'case',
        ['all', ['==', ['get', 'aiType'], 'population'], ['>', ['coalesce', ['get', 'population'], 0], 0]],
        ['concat', ['to-string', ['get', 'number']], '\n', ['to-string', ['get', 'population']], '人'],
        ['to-string', ['get', 'number']]
      ],
      'text-size': 14,
      'text-font': ['Noto Sans CJK JP Bold'],
      'text-allow-overlap': true,
      'text-line-height': 1.3
    },
    paint: {
      'text-color': [
        'match', ['get', 'aiType'],
        'population', '#7B1FA2', // AI人口プロファイル: 紫
        'risk', '#e53935',       // AI防災リスク診断: 赤
        '#2196f3'                // デフォルト: 青
      ],
      'text-halo-color': '#ffffff',
      'text-halo-width': 2
    }
  })

  // クリックハンドラーを追加
  if (!vm.shareFeaturesLayerAdded) {
    map.on('click', pointLayerId, vm.handleShareFeatureClick)
    map.on('click', polygonLayerId, vm.handleShareFeatureClick)
    map.on('mouseenter', pointLayerId, () => { map.getCanvas().style.cursor = 'pointer' })
    map.on('mouseleave', pointLayerId, () => { map.getCanvas().style.cursor = '' })
    map.on('mouseenter', polygonLayerId, () => { map.getCanvas().style.cursor = 'pointer' })
    map.on('mouseleave', polygonLayerId, () => { map.getCanvas().style.cursor = '' })
    vm.$store.commit('setShareFeaturesLayerAdded', true)
  }
}

// 共有地物クリック時の処理（3択ダイアログ表示）
export async function handleShareFeatureClick(vm: VM, e: any): Promise<void> {
  if (!e.features || e.features.length === 0) return
  const feature = e.features[0]
  const props = feature.properties
  const aiType = props.aiType
  const featureId = props.id

  // AI物件調査レポート+ (multiPointRanking) は dialog を出さず
  // 1. Vuex でハイライトイベント通知 (FW 側の行/マーカー連動)
  // 2. ストリートビュー FW を開く
  if (aiType === 'multiPointRanking') {
    // イベントバブリングを止めて他のクリックハンドラを抑制
    if (e.originalEvent) {
      e.originalEvent.stopPropagation()
      e.originalEvent.preventDefault?.()
    }
    const pointId = props.pointId
    if (pointId != null) {
      vm.$store.commit('setHighlightedRankingPointId', pointId)
    }
    // ストリートビューを開く (座標から)
    const coords = feature.geometry?.coordinates
    if (Array.isArray(coords) && coords.length >= 2) {
      vm.$store.state.streetViewCoords = {
        lng: coords[0],
        lat: coords[1],
        _t: Date.now()
      }
      vm.$store.dispatch('showFloatingWindow', 'street-view-window')
    }
    return
  }

  // aiType名を日本語化
  const aiTypeNames: Record<string, string> = {
    instant: 'AI瞬間レポート',
    risk: 'AI防災リスク診断',
    population: 'AI人口プロファイル',
    childcare: 'AI子育てレポート',
    'property-report': 'AI物件調査レポート'
  }
  const typeName = aiTypeNames[aiType] || 'AI分析'
  const isPolygon = feature.geometry.type === 'Polygon'

  // 3択ダイアログを表示
  vm.shareFeatureActionProps = { feature, aiType, typeName, isPolygon, featureId, props }
  const action = await new Promise(resolve => {
    vm.shareFeatureActionResolve = resolve
    vm.showShareFeatureActionDialog = true
  })

  if (action === 'open') {
    // レポートを開く（地物のtypeIndexを渡してFWのナンバリングを同期）
    const featureTypeIndex = props.typeIndex

    // aiType→FWタイプのマッピング
    const fwTypeMap: Record<string, string> = {
      instant: 'ai-point-info',
      risk: 'area-analysis',
      population: 'super-population',
      childcare: 'ai-childcare-report',
      'property-report': 'property-report'
    }
    const fwType = fwTypeMap[aiType]

    // 既存インスタンスを検索（同じtypeIndexのものがあれば再利用）
    const existingInstance = vm.$store.getters.findFWInstanceByTypeIndex(fwType, featureTypeIndex)

    if (existingInstance) {
      // 既存インスタンスを再表示（データ再取得しない）
      vm.$store.commit('showFWInstance', { type: fwType, id: existingInstance.id })
    } else {
      // 新規インスタンス作成
      if (aiType === 'instant') {
        const coords = feature.geometry.coordinates
        vm.$store.commit('addFWInstance', {
          type: 'ai-point-info',
          data: { lng: coords[0], lat: coords[1], tab: props.tab || 'realestate', fromShareFeature: true, typeIndex: featureTypeIndex }
        })
      } else if (aiType === 'risk') {
        const isPoint = feature.geometry.type === 'Point'
        const geojson = { type: 'Feature', geometry: feature.geometry, properties: {} }
        vm.$store.commit('addFWInstance', {
          type: 'area-analysis',
          data: {
            polygonData: isPoint ? null : geojson,
            pointData: isPoint ? geojson : null,
            analysisMode: isPoint ? 'point' : 'polygon',
            typeIndex: featureTypeIndex
          }
        })
      } else if (aiType === 'population') {
        const analysisMode = props.analysisMode || 'point'
        if (analysisMode === 'point') {
          // ポイントモード: 重心座標で復元
          let lng: number, lat: number
          if (feature.geometry.type === 'Polygon') {
            const centroid = turf.centroid(feature)
            lng = centroid.geometry.coordinates[0]
            lat = centroid.geometry.coordinates[1]
          } else {
            lng = feature.geometry.coordinates[0]
            lat = feature.geometry.coordinates[1]
          }
          vm.$store.commit('addFWInstance', {
            type: 'super-population',
            data: { lng, lat, fromShareFeature: true, typeIndex: featureTypeIndex, analysisMode: 'point' }
          })
        } else {
          // エリアモード（freehand/circle/polygon）: ポリゴンで復元
          const geojson = { type: 'Feature', geometry: feature.geometry, properties: {} }
          vm.$store.commit('addFWInstance', {
            type: 'super-population',
            data: { polygonData: geojson, fromShareFeature: true, typeIndex: featureTypeIndex, analysisMode }
          })
        }
      } else if (aiType === 'childcare') {
        // AI子育てレポート
        const coords = feature.geometry.coordinates
        vm.$store.commit('addFWInstance', {
          type: 'ai-childcare-report',
          data: { lng: coords[0], lat: coords[1], fromShareFeature: true, typeIndex: featureTypeIndex }
        })
      } else if (aiType === 'property-report') {
        // AI物件調査レポート
        const coords = feature.geometry.coordinates
        vm.$store.commit('addFWInstance', {
          type: 'property-report',
          data: { lng: coords[0], lat: coords[1], fromShareFeature: true, typeIndex: featureTypeIndex }
        })
      }
    }
  } else if (action === 'delete') {
    // 地物を削除 + 関連するFWインスタンスも削除
    const featureTypeIndex = props.typeIndex
    const fwTypeMap: Record<string, string> = {
      instant: 'ai-point-info',
      risk: 'area-analysis',
      population: 'super-population',
      childcare: 'ai-childcare-report',
      'property-report': 'property-report'
    }
    const fwType = fwTypeMap[aiType]
    const existingInstance = vm.$store.getters.findFWInstanceByTypeIndex(fwType, featureTypeIndex)
    if (existingInstance) {
      vm.$store.commit('removeFWInstance', { type: fwType, id: existingInstance.id })
    }
    vm.$store.commit('removeShareFeature', featureId)
    showShareFeaturesOnMap(vm)
  }
  // action === 'cancel' の場合は何もしない
}

// 共有地物アクションダイアログの結果を処理
export function resolveShareFeatureAction(vm: VM, action: string): void {
  vm.showShareFeatureActionDialog = false
  if (vm.shareFeatureActionResolve) {
    vm.shareFeatureActionResolve(action)
    vm.shareFeatureActionResolve = null
  }
}

// 共有地物全体が収まるようにfitBounds
export function fitToShareFeatures(vm: VM): void {
  if (vm.shareFeatures.length === 0) return
  const map = vm.$store.state.map
  if (!map) return

  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity

  vm.shareFeatures.forEach((f: any) => {
    if (f.geometry.type === 'Point') {
      const [lng, lat] = f.geometry.coordinates
      minLng = Math.min(minLng, lng)
      minLat = Math.min(minLat, lat)
      maxLng = Math.max(maxLng, lng)
      maxLat = Math.max(maxLat, lat)
    } else if (f.geometry.type === 'Polygon') {
      const coords = f.geometry.coordinates[0]
      coords.forEach(([lng, lat]: [number, number]) => {
        minLng = Math.min(minLng, lng)
        minLat = Math.min(minLat, lat)
        maxLng = Math.max(maxLng, lng)
        maxLat = Math.max(maxLat, lat)
      })
    }
  })

  if (minLng !== Infinity) {
    map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 50 })
  }
}

// 共有地物に追加（ストアのmutationを使用、updatePermalinkFireも自動発火）
export function addShareFeature(vm: VM, aiType: string, geometry: any, properties: any = {}): void {
  vm.$store.commit('addShareFeature', { aiType, geometry, properties })
  showShareFeaturesOnMap(vm)
}

// 共有地物から削除（ストアのmutationを使用、updatePermalinkFireも自動発火）
export function removeShareFeature(vm: VM, id: string): void {
  vm.$store.commit('removeShareFeature', id)
  showShareFeaturesOnMap(vm)
}
